import * as React from 'react';
import { useRecoilState } from 'recoil';

import * as api from '@gitbook/api-client';
import * as analytics from '@gitbook/app-analytics/client';
import * as hiveCore from '@gitbook/hive-core';
import { useHiveRevision } from '@gitbook/hive-react';
import {
    createPalette,
    PaletteButton,
    type PaletteCommandDef,
    PaletteCommandType,
    PalettePositionWidth,
} from '@gitbook/react-uikit';
import { Icon } from '@gitbook/spine-icons';
import {
    Button,
    ButtonGroup,
    ButtonSkeleton,
    DialogRoot,
    LoadingIndicator,
    useDialogTriggerState,
} from '@gitbook/spine-react';
import type { LoadingStateHookResult } from '@gitbook/util-react-hooks';
import { filterOutNullable } from '@gitbook/util-ts';

import { mergeStrategyAtom } from '../../state';
import { useGitSyncCurrentStatus } from '../GitSyncFlow';
import { GitSyncProviderName } from '../GitSyncProviders';
import { useImportRuns } from '../Imports';
import { MergeRulesDialog, useEvaluateMergeRules } from '../MergeRules';
import {
    GuidedTourPopover,
    GuidedTourReference,
    GuidedTourStep,
    useGuidedTour,
} from '../Onboarding';
import { useChangeRequestMergeActions } from './ChangeRequestMergeProvider';
import { ChangeRequestMergeRulesStatusButton } from './ChangeRequestMergeRulesStatus';
import { ChangeRequestOutOfDateButton } from './ChangeRequestOutOfDate';
import { ChangeRequestReviewRequestDialog } from './ChangeRequestReviewRequestDialog';

type ChangeRequestMergeButtonsProps = {
    space: api.Space;
    changeRequest: api.ChangeRequest;
    analyticsContext: analytics.AnalyticsContextSpaceContent;

    /**
     * If true, a button to summarize the merge rules status will be shown.
     */
    showMergeRulesStatus?: boolean;
};

export function ChangeRequestMergeButtons(props: ChangeRequestMergeButtonsProps) {
    const { revision } = useHiveRevision();

    const {
        onMerge: handleMerge,
        mergeChangeRequest,
        updateChangeRequest,
        mergeLoadable,
        updateLoadable,
        isOutOfDate,
    } = useChangeRequestMergeActions();
    const [loadingUpdate] = updateLoadable;
    const [loadingMerge] = mergeLoadable;
    const hasConflicts = hiveCore.isConflictRevision(revision);
    const confirmOutdatedMergeDialogTrigger = useDialogTriggerState();

    if (hasConflicts) {
        return null;
    }

    return (
        <>
            {isOutOfDate ? (
                <ChangeRequestOutOfDateButton
                    onUpdate={updateChangeRequest}
                    onMerge={mergeChangeRequest}
                    disabled={loadingMerge}
                    loadable={updateLoadable}
                    dialogTrigger={confirmOutdatedMergeDialogTrigger}
                />
            ) : null}

            <React.Suspense
                fallback={
                    // We need a suspense boundary as the evaluation of the merge rule can be async
                    <ButtonSkeleton label="Merge" />
                }
            >
                <ChangeRequestMergeButton
                    {...props}
                    loadable={mergeLoadable}
                    disabled={loadingUpdate}
                    onMerge={() => handleMerge(confirmOutdatedMergeDialogTrigger)}
                />
            </React.Suspense>
        </>
    );
}
/**
 * Button to merge a change request into its space.
 */
export function ChangeRequestMergeButton(
    props: ChangeRequestMergeButtonsProps & {
        disabled?: boolean;
        onMerge: () => Promise<void>;
        className?: string;
        loadable: LoadingStateHookResult;
    }
) {
    const {
        space,
        changeRequest,
        analyticsContext,
        disabled: propDisabled = false,
        onMerge,
        className,
        loadable,
        showMergeRulesStatus = false,
    } = props;

    const [isLoading] = loadable;

    const gitSyncStatus = useGitSyncCurrentStatus(space);
    const [mergeStrategy, setMergeStrategy] = useRecoilState(mergeStrategyAtom);
    const requestReviewDialogTrigger = useDialogTriggerState();

    const importRuns = useImportRuns({
        target: {
            space: space.id,
            changeRequest: changeRequest.id,
        },
    });
    const showImportGuidedTour = importRuns.length > 0;
    const disabledBecauseOfImport = importRuns.some((run) =>
        ['pending', 'in-progress'].includes(run.status)
    );
    const disabledBecauseOfImportMessaging = 'Merging is blocked while an import is running';

    const isGitSyncRunning = gitSyncStatus?.state === 'running';
    const latestGitSyncImportWasErrorneous = Boolean(
        space.gitSync?.installationProvider &&
            (gitSyncStatus?.state === 'failure' || gitSyncStatus?.state === 'timed-out') &&
            gitSyncStatus.direction === api.GitSyncOperationDirection.Import
    );

    // Merge button is disabled when the merge rule is rejected
    const mergeRulesEvaluation = useEvaluateMergeRules({ space, changeRequest });
    const disabledBecauseOfMergeRules = mergeRulesEvaluation.result === 'rejected';

    // Merge button is disabled when Git Sync is running
    const disabledBecauseOfGitSync = mergeStrategy === 'merge' && isGitSyncRunning;
    const disabledBecauseOfGitSyncMessaging = 'Git Sync in progress, please wait to merge';

    // Merge button is disabled when the latest import has failed
    const disabledBecauseOfGitImportError =
        mergeStrategy === 'merge' && latestGitSyncImportWasErrorneous;
    const provider = GitSyncProviderName[space.gitSync?.installationProvider ?? 'github'];
    const disabledBecauseOfGitImportErrorMessaging = `An error was detected while importing recent commits from the ${provider} repository, to resume synchronization and merging of change requests please fix the error.`;

    // Can request a review when the change request is in draft status
    const canRequestAReview = changeRequest.status === api.ChangeRequestStatus.Draft;
    const onRequestReview = () => {
        requestReviewDialogTrigger.open();
    };

    const mergeRulesDialogTrigger = useDialogTriggerState();

    const strategyConfigs = [
        {
            strategy: 'merge' as const,
            available:
                space.editMode === api.SpaceEditMode.Live
                    ? space.permissions.edit
                    : space.permissions.merge,
            title: 'Merge',
            subtitle: disabledBecauseOfMergeRules
                ? 'Merging is blocked because of merge rules'
                : disabledBecauseOfGitSync
                  ? disabledBecauseOfGitSyncMessaging
                  : disabledBecauseOfImport
                    ? disabledBecauseOfImportMessaging
                    : 'Instantly merge your changes.',
            icon: Icon.ChangeRequestMerged,
            onPress: onMerge,
            label: 'Merge',
            kind:
                disabledBecauseOfImport || isGitSyncRunning || latestGitSyncImportWasErrorneous
                    ? ('secondary' as const)
                    : ('merge' as const),
            disabledReason: disabledBecauseOfMergeRules
                ? `This change request does not meet the merge rule: ${mergeRulesEvaluation.rejectedRules[0].message}`
                : disabledBecauseOfGitSync
                  ? disabledBecauseOfGitSyncMessaging
                  : disabledBecauseOfGitImportError
                    ? disabledBecauseOfGitImportErrorMessaging
                    : disabledBecauseOfImport
                      ? disabledBecauseOfImportMessaging
                      : undefined,
        },
        {
            strategy: 'ready-for-review' as const,
            available: canRequestAReview,
            title: 'Request a review',
            subtitle: 'Notify and allow others to review before merging',
            icon: Icon.ChangeRequestOpen,
            onPress: onRequestReview,
            label: 'Request a review',
            kind: 'primary' as const,
            disabledReason: undefined,
        },
    ].filter((config) => config.available);

    // We show as selected only a strategy that can actually be used
    const workingStrategyConfigs = strategyConfigs.filter((config) => !config.disabledReason);
    const activeConfig =
        workingStrategyConfigs.find((config) => config.strategy === mergeStrategy) ??
        workingStrategyConfigs[0] ??
        strategyConfigs[0];

    const importTour = useGuidedTour('cr-import');

    if (!activeConfig) {
        return null;
    }

    const disabled = isLoading || propDisabled || Boolean(activeConfig.disabledReason);

    return (
        <div className="inline-flex gap-1">
            <GuidedTourStep tour="cr-import" skip={!showImportGuidedTour} step="start">
                <GuidedTourReference>
                    <ButtonGroup
                        kind="segmented"
                        disabled={disabled}
                        className={className}
                        separatorKind={activeConfig.kind}
                        border={false}
                    >
                        <Button
                            kind={activeConfig.kind}
                            leadingIcon={activeConfig.icon}
                            disabled={disabled}
                            tooltip={
                                activeConfig.disabledReason
                                    ? { label: activeConfig.disabledReason }
                                    : undefined
                            }
                            label={activeConfig.label}
                            loadable={loadable}
                            onPress={() => {
                                importTour.markAsDone();
                                return activeConfig.onPress();
                            }}
                        />

                        {strategyConfigs.length > 1 ? (
                            <PaletteButton
                                kind={activeConfig.kind}
                                leadingIcon={Icon.ChevronDown}
                                disabled={disabled}
                                palette={() =>
                                    createPalette({
                                        position: {
                                            width: PalettePositionWidth.LARGE,
                                            preferredPlacement: 'bottom',
                                            preferredAlignment: 'end',
                                        },
                                        groups: [
                                            {
                                                key: 'options',
                                                commands: strategyConfigs.map((config) => {
                                                    const command: PaletteCommandDef = {
                                                        key: config.strategy,
                                                        type: PaletteCommandType.RADIO,
                                                        selected:
                                                            activeConfig.strategy ===
                                                            config.strategy,
                                                        title: config.title,
                                                        subtitle: config.subtitle,
                                                        icon: isLoading
                                                            ? LoadingIndicator
                                                            : config.icon,
                                                        disabled:
                                                            disabled || !!config.disabledReason,
                                                        trigger: () =>
                                                            setMergeStrategy(config.strategy),
                                                    };
                                                    return command;
                                                }),
                                            },
                                            space.permissions.admin
                                                ? {
                                                      key: 'space.mergeRules',
                                                      commands: [
                                                          {
                                                              key: 'space.mergeRules',
                                                              icon: Icon.MergeRulesAllow,
                                                              title: 'Configure merge rules',
                                                              subtitle:
                                                                  'Define the rules for merging change requests',
                                                              trigger: () => {
                                                                  mergeRulesDialogTrigger.open();
                                                              },
                                                          },
                                                      ],
                                                  }
                                                : null,
                                        ].filter(filterOutNullable),
                                    })
                                }
                                iconOnly
                                tooltip={{
                                    disabled,
                                    label: 'Select option or configure merge rules',
                                }}
                                label="Select option or configure merge rules"
                                onPressStart={() => {
                                    analytics.logEvent(
                                        analytics.createChangeRequestEvent(
                                            space,
                                            changeRequest.id,
                                            {
                                                ...analyticsContext,
                                                type: 'change_request_pick_flows_open',
                                                spaceSyncStatus: isGitSyncRunning
                                                    ? 'syncing'
                                                    : 'idle',
                                            }
                                        )
                                    );
                                }}
                            />
                        ) : null}
                    </ButtonGroup>
                </GuidedTourReference>
                <GuidedTourPopover className="max-w-sm">
                    When the imported content is ready to go live, you can either{' '}
                    <strong>Merge</strong> it directly or <strong>Request a review</strong> if you’d
                    like someone to check it first.
                </GuidedTourPopover>
            </GuidedTourStep>
            {disabledBecauseOfMergeRules && showMergeRulesStatus ? (
                <ChangeRequestMergeRulesStatusButton
                    space={space}
                    changeRequest={changeRequest}
                    mergeRulesEvaluation={mergeRulesEvaluation}
                />
            ) : null}
            <DialogRoot {...requestReviewDialogTrigger.rootProps}>
                <ChangeRequestReviewRequestDialog changeRequest={changeRequest} space={space} />
            </DialogRoot>
            <DialogRoot {...mergeRulesDialogTrigger.rootProps}>
                <MergeRulesDialog space={space} />
            </DialogRoot>
        </div>
    );
}
